<template>
  <div>
    <div class="d-flex align-center ga-2">
      <h3 v-if="!hideTitle">PM02/PM03</h3>
      <DateField
        class="width-160"
        v-model="start"
        label="From"
        variant="outlined"
        density="compact"
        hide-details
        @change="getData"
      />
      <DateField
        class="width-160"
        v-model="end"
        label="To"
        variant="outlined"
        density="compact"
        hide-details
        @change="getData"
      />
      <v-autocomplete
        v-if="flocIds === undefined"
        class="width-160 compact-autocomplete"
        v-model="ampMasterIds"
        label="AMP"
        :items="ampMasters"
        item-value="id"
        item-title="name"
        multiple
        variant="outlined"
        density="compact"
        width="400px"
        max-width="400px"
        hide-details
        @update:model-value="getData"
      />
      <FLOCSearch
        v-if="flocIds === undefined"
        v-model="selectedFlocs"
        label="FLOC"
        variant="outlined"
        density="compact"
        width="400px"
        max-width="400px"
        hide-details
        multiple
        @update:model-value="getData"
      />
      <v-select
        class="width-160"
        v-model="groupBy"
        label="Group By"
        :items="groupBys"
        variant="outlined"
        density="compact"
        hide-details
        @update:model-value="getData"
      />
      <v-radio-group
        v-model="aggregate"
        inline
        hide-details
        @update:model-value="getData"
      >
        <v-radio
          v-for="aggregate in aggregates"
          :key="aggregate"
          :label="aggregate"
          :value="aggregate"
        />
      </v-radio-group>
      <v-spacer />
      <v-progress-circular v-if="fetchingCount > 0" indeterminate :size="25" />
    </div>

    <div class="mt-2" :style="{ height: chartHeight }">
      <Bar :data="data" :options="options" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  AssetManagementPlanMaster,
  DateObject,
  FunctionalLocation,
} from "@/classes";
import DateField from "@/components/base/inputs/DateField.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  BarElement,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { computed, onMounted, ref, watch } from "vue";
import { Bar } from "vue-chartjs";
import NuxtAxiosInstance from "@/plugins/axios";
import Decimal from "decimal.js";
import FLOCSearch from "../asset-management-plan/FLOCSearch.vue";
import { useListStore } from "@/store/lists";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const props = withDefaults(
  defineProps<{
    hideTitle?: boolean;
    chartHeight?: string;
    flocIds?: string[];
  }>(),
  {
    chartHeight: "600px",
  },
);

const $axios = NuxtAxiosInstance;
const lists = useListStore();

const ampMasters = lists.useList<AssetManagementPlanMaster>(
  "assetManagementPlanMasters",
);

const aggregates = ["Count", "Cost"] as const;
type Aggregate = (typeof aggregates)[number];

const groupBys = ["Month", "Year"] as const;
type GroupBy = (typeof groupBys)[number];

type PM02PM03Metrics = {
  pm02: Record<string, Decimal>;
  pm03: Record<string, Decimal>;
};

const start = ref<DateObject | null>(
  DateObject.add(new DateObject(), { years: -1 }),
);
const end = ref<DateObject | null>(new DateObject());
const ampMasterIds = ref<string[]>([]);
const selectedFlocs = ref<FunctionalLocation[]>([]);
const aggregate = ref<Aggregate>("Count");
const groupBy = ref<GroupBy>("Year");

const abortController = ref(new AbortController());
const fetchingCount = ref(0);
const metrics = ref<PM02PM03Metrics>({
  pm02: {},
  pm03: {},
});

onMounted(() => getData());

watch(
  () => props.flocIds,
  () => getData(),
);

const getData = async () => {
  try {
    abortController.value.abort();
    abortController.value = new AbortController();
    fetchingCount.value++;
    metrics.value = await $axios.$post<PM02PM03Metrics>(
      "/metrics/pm02-pm03",
      {
        start: start.value,
        end: end.value,
        aggregate: aggregate.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: props.flocIds ?? selectedFlocs.value.map((f) => f.id),
        group_by: groupBy.value,
      },
      {
        signal: abortController.value.signal,
      },
    );
  } catch (e) {
    // Aborted
  } finally {
    fetchingCount.value--;
  }
};

const data = computed<ChartData<"bar">>(() => {
  const keySet = new Set<string>();
  Object.keys(metrics.value.pm02).forEach((key) => {
    keySet.add(key);
  });
  Object.keys(metrics.value.pm03).forEach((key) => {
    keySet.add(key);
  });

  const keys = Array.from(keySet).sort();
  const pm02Values: number[] = [];
  const pm03Values: number[] = [];
  for (const key of keys) {
    const pm02 = metrics.value.pm02[key] ?? new Decimal(0);
    const pm03 = metrics.value.pm03[key] ?? new Decimal(0);
    pm02Values.push(pm02.toNumber());
    pm03Values.push(pm03.toNumber());
  }

  return {
    labels: keys,
    datasets: [
      {
        label: "PM02",
        data: pm02Values,
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 3,
        fill: true,
        backgroundColor: "rgb(75, 192, 192, 0.2)",
        tension: 0,
      },
      {
        label: "PM03",
        data: pm03Values,
        borderColor: "rgb(245, 142, 47)",
        borderWidth: 3,
        fill: true,
        backgroundColor: "rgb(245, 142, 47, 0.2)",
        tension: 0,
      },
    ],
  };
});

const options = computed<ChartOptions<"bar">>(() => {
  const prefix = aggregate.value === "Cost" ? "$" : "";
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        min: 0,
        stacked: true,
        ticks: {
          callback: (value) => {
            return `${prefix}${value.toLocaleString()}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (item) => {
            return `${
              item.dataset.label
            }: ${prefix}${item.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
  };
});
</script>

<style scoped>
.compact-autocomplete :deep(.v-field__field) {
  overflow-y: auto;
  height: 40px;
}
</style>
